/**
 * Whether a resource can take a booking at a given time, applied to a drag, a
 * move or an assignment from the Unallocated Bookings page. Like the booking
 * form, it lets a booking overlap the resource's other bookings; only the
 * contract, the working profile, bank holidays and absences refuse it.
 *
 * A resource with no working profile of its own is judged against the
 * company's, exactly as the planner and the heatmap do.
 */
import type { Absence, BankHoliday, Resource, WorkingHoursProfile } from '@/types';
import {
  dayKey,
  effectiveWorkingProfile,
  formatDayKeyForDisplay,
  profileHoursForDate,
} from '@/lib/slotEditValidation';
import { resourceUnavailableDays } from '@/lib/resourceUnavailableDays';
import {
  dayIntervalsFromWindows,
  minutesToHHMM,
  overlapsBooking,
  DEFAULT_BUSINESS_HOURS_END,
  DEFAULT_BUSINESS_HOURS_START,
} from '@/lib/dayAvailability';
import { UNALLOCATED_RESOURCE_ID } from '@/utils/unallocated-booking';
import { firstDayOutsideContract } from '@/lib/contractWindow';

const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;
const MINUTES_PER_DAY = 1440;

export type ResourceAvailabilityReason =
  | 'non-working-day'
  | 'day-unavailable'
  | 'day-over-capacity'
  | 'out-of-contract';

export interface ResourceAvailabilityResult {
  allowed: boolean;
  /** Why the assignment was refused, for a toast. Absent when allowed. */
  message?: string;
  reason?: ResourceAvailabilityReason;
}

export interface ResourceAvailabilityInput {
  resource: Pick<
    Resource,
    'id' | 'name' | 'useCustomWorkingProfile' | 'customWorkingProfile' | 'contractStartDate' | 'contractEndDate'
  >;
  startTime: Date;
  endTime: Date;
  globalWorkingProfile?: WorkingHoursProfile;
  absences?: Absence[];
  bankHolidays?: BankHoliday[];
  businessHoursStart?: number;
  businessHoursEnd?: number;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** The part of a day the booking covers, as minutes since that day's midnight. */
function dayIntervalFor(day: Date, startTime: Date, endTime: Date) {
  const dayStart = day.getTime();
  const dayEnd = dayStart + MS_PER_DAY;
  const from = Math.max(dayStart, startTime.getTime());
  const to = Math.min(dayEnd, endTime.getTime());
  if (to <= from) return null;
  return {
    startMinutes: Math.round((from - dayStart) / 60_000),
    endMinutes: Math.min(MINUTES_PER_DAY, Math.round((to - dayStart) / 60_000)),
  };
}

/**
 * Whether every day the booking covers falls inside the resource's contract.
 * Applies to every booking type, auto-adjusting ones included, and to moves
 * that keep the booking on the same resource.
 */
export function checkContractWindow(
  resource: Pick<Resource, 'id' | 'name' | 'contractStartDate' | 'contractEndDate'>,
  startTime: Date,
  endTime: Date,
): ResourceAvailabilityResult {
  if (String(resource.id) === UNALLOCATED_RESOURCE_ID) return { allowed: true };
  const outsideDay = firstDayOutsideContract(resource, startTime, endTime);
  if (!outsideDay) return { allowed: true };
  const who = resource.name || 'This resource';
  const beforeStart = Boolean(resource.contractStartDate && outsideDay < resource.contractStartDate);
  const bound = beforeStart
    ? `starts on ${formatDayKeyForDisplay(resource.contractStartDate!)}`
    : `ends on ${formatDayKeyForDisplay(resource.contractEndDate!)}`;
  return {
    allowed: false,
    reason: 'out-of-contract',
    message: `${who}'s contract ${bound}, so they cannot be booked on ${formatDayKeyForDisplay(outsideDay)}`,
  };
}

/**
 * Whether the resource can take the booking over the given time, and why not
 * when it cannot. An Unallocated target is always allowed — it is a placeholder
 * for work nobody is assigned to yet, not a person with a calendar.
 */
export function canAssignBookingToResource(
  input: ResourceAvailabilityInput,
): ResourceAvailabilityResult {
  const {
    resource, startTime, endTime, globalWorkingProfile, absences = [], bankHolidays = [],
    businessHoursStart = DEFAULT_BUSINESS_HOURS_START,
    businessHoursEnd = DEFAULT_BUSINESS_HOURS_END,
  } = input;

  const resourceId = String(resource.id);
  if (resourceId === UNALLOCATED_RESOURCE_ID) return { allowed: true };
  if (isNaN(startTime.getTime()) || isNaN(endTime.getTime()) || endTime <= startTime) {
    return { allowed: true };
  }

  const contract = checkContractWindow(resource, startTime, endTime);
  if (!contract.allowed) return contract;

  const who = resource.name || 'This resource';
  const workingProfile = effectiveWorkingProfile(resource, globalWorkingProfile);

  const unavailableDays = resourceUnavailableDays({
    resourceId,
    startDate: startTime,
    endDate: endTime,
    bankHolidays,
    absences,
    workingProfile,
    businessHoursStart,
    businessHoursEnd,
  });
  const lossByDay = new Map(unavailableDays.map(day => [day.date.slice(0, 10), day]));

  let cursor = startOfDay(startTime);
  const lastDay = startOfDay(new Date(endTime.getTime() - 1));
  while (cursor <= lastDay) {
    const key = dayKey(cursor);
    const when = formatDayKeyForDisplay(key);
    const contracted = profileHoursForDate(cursor, workingProfile);
    if (contracted <= 0) {
      return {
        allowed: false,
        reason: 'non-working-day',
        message: `${who} does not work on ${when}`,
      };
    }

    const interval = dayIntervalFor(cursor, startTime, endTime);
    const loss = lossByDay.get(key);
    if (loss) {
      const lostWindows = loss.lostWindows ? dayIntervalsFromWindows(loss.lostWindows) : [];
      if (loss.lostHours === undefined || loss.lostHours >= contracted) {
        return {
          allowed: false,
          reason: 'day-unavailable',
          message: `${who} is not available on ${when} (${loss.reason})`,
        };
      }
      const clash = interval
        ? overlapsBooking(lostWindows, interval.startMinutes, interval.endMinutes)
        : null;
      if (clash) {
        return {
          allowed: false,
          reason: 'day-unavailable',
          message: `${who} is not available ${minutesToHHMM(clash.startMinutes)}–`
            + `${minutesToHHMM(clash.endMinutes)} on ${when} (${loss.reason})`,
        };
      }
    }

    const lostHours = loss ? (loss.lostHours ?? contracted) : 0;
    const remaining = Math.max(0, contracted - lostHours);
    // A full-day booking is stored as the whole calendar day, so the hours it
    // asks of the day are the ones the resource is contracted for, not 24.
    const spannedHours = interval
      ? (interval.endMinutes - interval.startMinutes) / 60
      : (Math.min(endTime.getTime(), cursor.getTime() + MS_PER_DAY)
        - Math.max(startTime.getTime(), cursor.getTime())) / MS_PER_HOUR;
    const requested = Math.min(spannedHours, contracted);
    if (loss && Math.round(requested * 10) / 10 > Math.round(remaining * 10) / 10) {
      return {
        allowed: false,
        reason: 'day-over-capacity',
        message: `This booking's hours on ${when} exceed the ${Math.round(remaining * 10) / 10}h`
          + ` ${who} has left that day (${loss.reason})`,
      };
    }

    cursor = new Date(cursor.getTime() + MS_PER_DAY);
  }

  return { allowed: true };
}
